/* Adenzo mobile app layer — runtime foundation (phase 1).
   The html.adenzo-app / html.adenzo-standalone classes are first set by a tiny inline script in
   layout/theme.liquid (before paint, to avoid a flash). This file keeps them in sync when the phone
   rotates or the window changes, and exposes a small API that later phases (tab bar, sheets,
   install prompt) build on. It never runs on desktop layouts: isApp() is false there. */
(function () {
  'use strict';
  if (window.AdenzoApp) return;

  var root = document.documentElement;
  var phoneQuery = window.matchMedia('(max-width: 749px) and (pointer: coarse)');
  var standaloneQuery = window.matchMedia('(display-mode: standalone)');

  function isStandalone() {
    return standaloneQuery.matches || window.navigator.standalone === true;
  }

  function isApp() {
    return phoneQuery.matches;
  }

  function sync() {
    var wasApp = root.classList.contains('adenzo-app');
    root.classList.toggle('adenzo-app', isApp());
    root.classList.toggle('adenzo-standalone', isStandalone());
    if (wasApp !== isApp()) {
      document.dispatchEvent(new CustomEvent('adenzo:app-mode', { detail: { app: isApp() } }));
    }
  }

  function listen(query) {
    if (query.addEventListener) query.addEventListener('change', sync);
    else if (query.addListener) query.addListener(sync); // older Safari
  }

  // Form posts (login, newsletter, checkout) cannot use cross-document view transitions; skipping them
  // avoids a harmless but noisy "Transition was aborted" console error. Normal link navigations still fade.
  var formNav = false;
  document.addEventListener('submit', function () {
    formNav = true;
    window.setTimeout(function () { formNav = false; }, 1500); // a prevented (AJAX) submit is not a navigation
  }, true);
  window.addEventListener('pageswap', function (event) {
    if (formNav && event.viewTransition && event.viewTransition.skipTransition) {
      try { event.viewTransition.skipTransition(); } catch (e) { /* nothing to skip */ }
    }
  });

  /* ---- loading feedback ------------------------------------------------------------------
     A thin progress bar runs along the top while a navigation is in flight (links, forms, tabs). Without it the
     old page just sits there after a tap and nothing seems to happen, especially in the installed app, which
     has no browser loading indicator. */
  var bar = null;
  var barTimer = 0;

  function barEl() {
    if (bar) return bar;
    bar = document.createElement('div');
    bar.className = 'adenzo-progress';
    bar.setAttribute('aria-hidden', 'true');
    bar.appendChild(document.createElement('span'));
    document.body.appendChild(bar);
    return bar;
  }

  function progressStart() {
    if (!isApp() || !document.body) return;
    var el = barEl();
    el.classList.remove('is-done', 'is-active');
    void el.offsetWidth; // restart the transition from 0
    el.classList.add('is-active');
    window.clearTimeout(barTimer);
    barTimer = window.setTimeout(progressDone, 15000); // a navigation that never happened (download, cancelled)
  }

  function progressDone() {
    if (!bar) return;
    window.clearTimeout(barTimer);
    bar.classList.remove('is-active');
    bar.classList.add('is-done');
  }

  function willNavigate(link) {
    var raw = link.getAttribute('href') || '';
    if (!raw || raw.charAt(0) === '#' || /^(javascript|mailto|tel|sms|whatsapp):/i.test(raw)) return false;
    if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return false;
    var url;
    try { url = new URL(link.href, window.location.href); } catch (e) { return false; }
    if (!/^https?:$/.test(url.protocol)) return false;
    return !(url.origin === window.location.origin && url.pathname === window.location.pathname && url.search === window.location.search && url.hash);
  }

  // Bubble phase and registered before the other app scripts, so anything that handles the click itself
  // (and calls preventDefault) has already run and is left alone.
  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var link = event.target.closest ? event.target.closest('a[href]') : null;
    if (link && willNavigate(link)) progressStart();
  });
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!event.defaultPrevented && form && !(form.target && form.target !== '_self')) progressStart();
  });
  window.addEventListener('pageshow', function (event) { if (event.persisted) progressDone(); });

  /* ---- lazy images: mark a product image's frame once the picture has arrived, so the placeholder shimmer
     (adenzo-app.css) shows only while it is still loading, including images that load as you scroll. */
  function markMedia(img) {
    var frame = img.closest ? img.closest('.media') : null;
    if (frame) frame.classList.add('adenzo-media-ready');
  }
  document.addEventListener('load', function (event) {
    if (event.target && event.target.tagName === 'IMG') markMedia(event.target);
  }, true);
  document.addEventListener('error', function (event) {
    if (event.target && event.target.tagName === 'IMG') markMedia(event.target);
  }, true);
  function markLoaded() {
    var images = document.querySelectorAll('.card__media img');
    for (var i = 0; i < images.length; i++) if (images[i].complete) markMedia(images[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', markLoaded); else markLoaded();

  /* ---- infinite scroll: while the next page of products is being fetched (the load-more link reads "Loading")
     the pagination block gets .adenzo-loading, which shows a spinner. */
  function watchPagination() {
    var pagination = document.getElementById('AjaxinatePagination');
    if (!pagination || !window.MutationObserver) return;
    var update = function () {
      var current = document.getElementById('AjaxinatePagination');
      var link = current && current.querySelector('.load_more_btn a');
      if (current) current.classList.toggle('adenzo-loading', !!link && /^loading/i.test(link.textContent.trim()));
    };
    new MutationObserver(update).observe(pagination.parentNode || pagination, { subtree: true, childList: true, characterData: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchPagination); else watchPagination();

  listen(phoneQuery);
  listen(standaloneQuery);

  window.AdenzoApp = {
    isApp: isApp,
    isStandalone: isStandalone,
    /** Top progress bar: shown automatically for link/form navigations; call these for other slow work. */
    progress: { start: progressStart, done: progressDone },
    /** Short vibration tick on Android; silently nothing on iPhone / desktop / reduced motion. */
    haptic: function (ms) {
      try {
        if (!isApp() || !window.navigator.vibrate) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        window.navigator.vibrate(ms || 10);
      } catch (e) { /* not supported */ }
    },
    isIOS: function () {
      return /iphone|ipad|ipod/i.test(window.navigator.userAgent) ||
        (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    }
  };

  sync();
  document.dispatchEvent(new CustomEvent('adenzo:app-ready'));
})();
